import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { CSV_UPLOAD_TYPES } from "@/constants/common.constant";
import { sampleCsvData } from "@/constants/csvSampleData";
import { APIClient } from "@/api";

interface BulkUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  type: "store" | "offer" | "user";
  onUploadComplete: (data: any[]) => void;
  children?: React.ReactNode; // Added children prop
  uploadType: CSV_UPLOAD_TYPES;
  merchantCode?: string;
}

export function BulkUploadModal({
  open,
  onOpenChange,
  title,
  type,
  onUploadComplete,
  uploadType,
  merchantCode,
  children,
}: BulkUploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const { data, titles, fileName, apiEndPoint } =
    sampleCsvData[uploadType] || {};
  const titleKeys = Object.keys(titles);

  // Function to validate file format
  const validateFileFormat = (file: File): boolean => {
    const allowedExtensions = [".csv", ".xlsx", ".xls"];
    const fileName = file.name.toLowerCase();
    return allowedExtensions.some((ext) => fileName.endsWith(ext));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];

      // Clear previous error
      setFileError("");

      // Validate file format
      if (!validateFileFormat(selectedFile)) {
        setFileError(
          "Please select a valid file format. Only CSV, XLSX, and XLS files are allowed."
        );
        setFile(null);
        // Clear the input
        e.target.value = "";
        return;
      }

      setFile(selectedFile);
    }
  };
  const downloadTemplate = ({ titleKeys, data, fileName }) => {
    const csvRows: string[] = [];

    csvRows.push(titleKeys.join(","));

    const values = Object.values(data);
    if (Array.isArray(values)) {
      values.forEach((row: any) => {
        const csvRow = titleKeys.map((key) => row[key] ?? "").join(",");
        csvRows.push(csvRow);
      });
    }

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", fileName || "sample.csv");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    if (merchantCode && uploadType === CSV_UPLOAD_TYPES.OFFER) {
      formData.append("merchantCode", merchantCode);
    } else if (merchantCode) {
      formData.append("merchantCode", merchantCode);
    }

    try {
      const { data } = await APIClient(apiEndPoint, formData, {
        requestType: "POST",
        contentType: "multipart/form-data",
      });
      setFile(null);
      onUploadComplete(data);
      setFile(null);
      onOpenChange(false);
    } catch (error) {
      toast.error(error?.message || "Failed to upload file");
    }
  };

  const handleDownloadTemplate = () => {
    downloadTemplate({ titleKeys, data, fileName });
    toast.success(`${fileName} template downloaded`);
  };

  // Clear error when modal opens/closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setFileError("");
      setFile(null);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Render children if provided */}
          {children || (
            <>
              <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded-md text-sm">
                <p className="font-medium mb-1">Instructions:</p>
                <ol className="list-decimal pl-4 space-y-1">
                  <li>Download the template below</li>
                  <li>
                    Fill in the{" "}
                    {type === "store"
                      ? "store"
                      : type === "offer"
                      ? "offer"
                      : "users"}{" "}
                    details in the spreadsheet
                  </li>
                  <li>Save the file and upload it here</li>
                </ol>
              </div>

              <Button
                variant="outline"
                size="sm"
                className="mb-6 w-full flex items-center justify-center"
                onClick={handleDownloadTemplate}
              >
                <Download className="h-4 w-4 mr-2" /> Download Template
              </Button>
            </>
          )}

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="file">Select File</Label>
            <div className="flex items-center gap-2">
              <Input
                id="file"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="flex-1"
              />
              <Button
                onClick={handleUpload}
                disabled={!file || !!fileError}
                className="flex items-center gap-1"
              >
                <Upload className="h-4 w-4" />
                Upload
              </Button>
            </div>
            {file && (
              <p className="text-xs text-muted-foreground">
                Selected file: {file.name}
              </p>
            )}
            {fileError && (
              <p className="text-xs text-red-600 mt-1">{fileError}</p>
            )}
          </div>

          <div className="bg-muted p-3 rounded-md">
            <p className="text-sm font-medium">File requirements:</p>
            <ul className="text-xs text-muted-foreground list-disc pl-4 mt-1 space-y-1">
              <li>CSV or Excel (.xlsx, .xls) format</li>
              <li>Maximum file size: 5MB</li>
              <li>Must follow template structure</li>
              <li>
                Maximum{" "}
                {type === "store"
                  ? "100 stores"
                  : type === "user"
                  ? "100 users"
                  : "500 offers"}{" "}
                per upload
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter className="sm:justify-end">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Helper component
const Separator = () => <div className="h-px bg-border my-2" />;
